/**
 * Aggregates all item list lines for a project (from getItemLists index payload).
 * Return lists apply a negative sign to quantity and value totals.
 */

export function getItemListTypeSign(list) {
  return list?.list_type === "return" ? -1 : 1;
}

function num(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {Array<object>} itemLists
 * @returns {{ totalQuantity: number, totalPurchase: number, totalSales: number, rowsByItemNumber: Array<object> }}
 */
export function aggregateProjectItemLists(itemLists) {
  let totalQuantity = 0;
  let totalPurchase = 0;
  let totalSales = 0;

  /** @type {Map<string, { key: string, item_number: string|null, product_title: string, quantity: number, total_purchase: number, total_sales: number }>} */
  const byKey = new Map();

  for (const list of itemLists || []) {
    const sign = getItemListTypeSign(list);
    const lineItems = list.item_list_items || [];

    for (const row of lineItems) {
      const oi = row.order_item;
      if (!oi) continue;

      const q = num(row.quantity) * sign;
      const unitPurchase = num(oi.purchase_price);
      const unitSales = num(oi.sales_price);

      totalQuantity += q;
      totalPurchase += q * unitPurchase;
      totalSales += q * unitSales;

      const p = oi.product;
      const hasNumber =
        p?.item_number != null && String(p.item_number).trim() !== "";
      const numberStr = hasNumber ? String(p.item_number).trim() : null;
      const groupKey = hasNumber
        ? `n:${numberStr}`
        : `p:${p?.id != null ? p.id : `oi:${oi.id}`}`;

      const title =
        p?.displayTitle || oi.title || numberStr || `— #${oi.id}`;

      if (!byKey.has(groupKey)) {
        byKey.set(groupKey, {
          key: groupKey,
          item_number: numberStr,
          product_title: title,
          quantity: 0,
          total_purchase: 0,
          total_sales: 0,
        });
      }
      const agg = byKey.get(groupKey);
      agg.quantity += q;
      agg.total_purchase += q * unitPurchase;
      agg.total_sales += q * unitSales;
    }
  }

  const rowsByItemNumber = Array.from(byKey.values()).sort((a, b) => {
    const an = a.item_number != null ? a.item_number : `zzz${a.product_title}`;
    const bn = b.item_number != null ? b.item_number : `zzz${b.product_title}`;
    return an.localeCompare(bn, undefined, { numeric: true, sensitivity: "base" });
  });

  return {
    totalQuantity,
    totalPurchase,
    totalSales,
    rowsByItemNumber,
  };
}

export function formatNok(amount) {
  return new Intl.NumberFormat("no-NO", {
    style: "currency",
    currency: "NOK",
  }).format(amount);
}
